/**
 * Sanitize a user-supplied post-auth redirect target down to an internal
 * app path. `redirect_url` query params (and router locations echoed into
 * them) are attacker-controllable, so anything that could escape the origin
 * is rejected: protocol-relative URLs ("//evil.com"), their backslash
 * spelling ("/\evil.com" — browsers treat "\" as "/" when parsing URLs),
 * absolute URLs ("https://evil.com"), scheme handlers ("javascript:…"), and
 * whitespace/control characters some parsers strip before resolving.
 */
export function sanitizeRedirectPath(
  raw: string | null | undefined,
  fallback = "/",
): string {
  if (!raw) return fallback;
  // Internal single-slash paths only: "//host" is protocol-relative and
  // "/\host" its backslash equivalent; anything not starting with "/" is a
  // relative path or a scheme ("javascript:", "https:").
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) {
    return fallback;
  }
  // No whitespace, control characters, or backslashes anywhere — they only
  // appear in smuggling attempts ("/\t//evil.com"), never in real app paths.
  if (/[\s\\\u0000-\u001f\u007f]/.test(raw)) return fallback;
  return raw;
}
